// pages/sales/AddSalePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import {
    Trash2,
    Plus,
    ArrowLeft,
    Save,
    X,
    RotateCcw,
} from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleActions } from '../../core/action/saleActions';
import { customerActions } from '../../core/action/customerActions';
import { productActions } from '../../core/action/productActions';
import AddCustomer from '../../core/modals/peoples/AddCustomer';

const AddSalePage = () => {
    const navigate = useNavigate();
    const searchInputRef = useRef(null);
    const productSearchTimeout = useRef(null);
    const searchDropdownRef = useRef(null);

    // Generate unique invoice/bill number
    const generateBillNumber = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `Bill L${year}${month}${day}-S${random}`;
    };

    const generateReferenceNo = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `INV${year}${month}${day}${random}`;
    };

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    // Initial state
    const [formData, setFormData] = useState({
        bill_number: generateBillNumber(),
        sales_reference: generateReferenceNo(),
        sale_date: dayjs().format('YYYY-MM-DD'),
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        no_of_items: 0,
        total_qty: 0,
        exd_tax_total: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        subtotal: 0,
        spl_disc_percent: 0,
        discount_amount: 0,
        adjustment: 0,
        additional_charge: 0,
        courier_charge: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        po_number: '',
        dc_number: '',
        remarks: '',
        status: 'pending',
        payment_mode: '',
        payment_status: 'unpaid',
        payment_history: [],
        items: []
    });

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [outOfStockMessage, setOutOfStockMessage] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerError, setCustomerError] = useState('');
    // Raw string values for numeric inputs (allows typing 0.00045 etc.)
    const [rawInputValues, setRawInputValues] = useState({});

    // Cache for product searches
    const productCache = useRef(new Map());

    // Close product search dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
                setShowProductSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment mode options
    const paymentModeOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'card', label: 'Card' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'upi', label: 'UPI' },
        { value: 'cheque', label: 'Cheque' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' },
        { value: 'advance_payment', label: 'Advance Payment' },
        { value: 'against_payment', label: 'Against Payment' },
        { value: 'pay_in_15', label: 'Pay in 15 Days' },
        { value: 'pay_in_30', label: 'Pay in 30 Days' },
        { value: 'pay_in_45', label: 'Pay in 45 Days' }
    ];

    // Credit-term statuses that should not be auto-overridden when paid amount is 0
    const CREDIT_TERM_STATUSES = ['pay_in_15', 'pay_in_30', 'pay_in_45'];

    // Re-sync paid/due amounts to the (possibly changed) grand total based on the
    // current payment status. Called after any change to totals (qty, price,
    // discount, charges, rounding) so the Paid Amount field stays correct.
    const reconcilePayment = (state) => {
        const grandTotal = state.enable_rounding ? state.rounded_grand_total : state.grand_total;
        const status = state.payment_status;
        let paid_amount = parseFloat(state.paid_amount) || 0;
        let due_amount = parseFloat(state.due_amount) || 0;

        if (status === 'paid' || status === 'advance_payment' || status === 'against_payment') {
            // Full payment — paid follows the grand total
            paid_amount = grandTotal;
            due_amount = 0;
        } else if (status === 'unpaid' || CREDIT_TERM_STATUSES.includes(status)) {
            // No payment now — full amount is due
            paid_amount = 0;
            due_amount = grandTotal;
        } else {
            // 'partial' or anything else — keep paid (clamped), recompute due
            paid_amount = Math.min(paid_amount, grandTotal);
            due_amount = grandTotal - paid_amount;
        }

        // Keep a single initial payment-history entry in sync with the paid amount.
        // Always carry method + date so the saved record (and Edit view) show them.
        let payment_history = [];
        if (paid_amount > 0 && state.payment_mode) {
            const prevEntry = Array.isArray(state.payment_history) && state.payment_history.length === 1
                ? state.payment_history[0]
                : null;
            payment_history = [{
                ...(prevEntry || {}),
                date: state.sale_date,
                amount: paid_amount,
                method: state.payment_mode,
                reference: prevEntry?.reference ?? null,
                notes: prevEntry?.notes || 'Initial payment',
                status: 'completed'
            }];
        }

        return { ...state, paid_amount, due_amount, payment_history };
    };

    // Load customers
    const loadCustomers = useCallback(async (search = '') => {
        try {
            setLoadingCustomers(true);
            setCustomerError('');

            const params = { limit: 50 };
            if (search) {
                params.search = search;
            }

            const result = await customerActions.getCustomers(params);

            if (result.success) {
                if (result.data && result.data.length > 0) {
                    const customerList = result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} ${c.phone ? `(${c.phone})` : ''}`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }));
                    setCustomers(customerList);
                } else {
                    setCustomers([]);
                }
            } else {
                setCustomerError('Failed to load customers');
                console.error('Error loading customers:', result.message);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setCustomerError('Error loading customers');
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    // Load initial customers
    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Handle customer search
    const handleCustomerSearch = (inputValue) => {
        if (inputValue.length >= 2) {
            loadCustomers(inputValue);
        }
        return inputValue;
    };

    // Fetch last price for customer-product
    const fetchLastPriceForCustomer = async (customerId, productId) => {
        if (!customerId || !productId) return null;

        try {
            const result = await productActions.getLastPrice(customerId, productId);
            if (result.success && result.data && result.data.last_price > 0) {
                return result.data;
            }
        } catch (error) {
            console.error('Error fetching last price:', error);
        }
        return null;
    };

    // Optimized product search with caching and last price
    const searchProducts = async (term) => {
        if (term.length < 1) {
            setSearchResults([]);
            setShowProductSearch(false);
            setSearching(false);
            setOutOfStockMessage('');
            return;
        }

        // Check cache first
        if (productCache.current.has(term)) {
            const cached = productCache.current.get(term);
            setSearchResults(cached);
            setShowProductSearch(true);
            setSearching(false);
            return;
        }

        try {
            setSearching(true);
            const result = await productActions.searchProducts(term);

            if (result.success) {
                const termLower = term.toLowerCase();
                const allResults = result.data || [];
                const inStockResults = allResults
                    .filter(p => parseFloat(p.quantity) > 0)
                    .sort((a, b) => {
                        const aName = (a.product_name || '').toLowerCase();
                        const bName = (b.product_name || '').toLowerCase();
                        const aSku = (a.sku || '').toLowerCase();
                        const bSku = (b.sku || '').toLowerCase();

                        const aStartsWith = aName.startsWith(termLower) || aSku.startsWith(termLower);
                        const bStartsWith = bName.startsWith(termLower) || bSku.startsWith(termLower);

                        if (aStartsWith && !bStartsWith) return -1;
                        if (!aStartsWith && bStartsWith) return 1;

                        return aName.localeCompare(bName);
                    })
                    .slice(0, 20);

                // If matches exist but all are out of stock, notify the user
                if (inStockResults.length === 0 && allResults.length > 0) {
                    setOutOfStockMessage('Matching product(s) found, but stock is 0. Cannot add out-of-stock items.');
                } else {
                    setOutOfStockMessage('');
                }

                // If customer is selected, fetch last price for each product
                if (selectedCustomer && selectedCustomer.value) {
                    const enhancedResults = await Promise.all(inStockResults.map(async (product) => {
                        const lastPriceData = await fetchLastPriceForCustomer(selectedCustomer.value, product.id);
                        return {
                            ...product,
                            last_price_data: lastPriceData,
                            display_price: lastPriceData ? lastPriceData.last_price : product.selling_price,
                            has_last_price: !!lastPriceData
                        };
                    }));
                    productCache.current.set(term, enhancedResults);
                    setSearchResults(enhancedResults);
                } else {
                    productCache.current.set(term, inStockResults);
                    setSearchResults(inStockResults);
                }
                setShowProductSearch(true);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Search products with debounce
    const handleProductSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        // Clear previous timeout
        if (productSearchTimeout.current) {
            clearTimeout(productSearchTimeout.current);
        }

        if (term.length < 1) {
            setSearchResults([]);
            setShowProductSearch(false);
            setSearching(false);
            return;
        }

        // Set new timeout
        productSearchTimeout.current = setTimeout(() => {
            searchProducts(term);
        }, 300);
    };

    // Calculate all totals with rounding
    const calculateTotals = useCallback((items, splDiscPercent = 0, adjustment = 0, additionalCharge = 0, courierCharge = 0, enableRounding = true) => {
        const no_of_items = items.length;
        const total_qty = items.reduce((sum, i) => sum + (parseFloat(i.quantity || 0)), 0);

        const subtotal = items.reduce((sum, i) => sum + (parseFloat(i.unit_price || 0) * parseFloat(i.quantity || 0)), 0);

        const total_sgst = items.reduce((sum, i) => sum + parseFloat(i.sgst_amount || 0), 0);
        const total_cgst = items.reduce((sum, i) => sum + parseFloat(i.cgst_amount || 0), 0);
        const total_igst = items.reduce((sum, i) => sum + parseFloat(i.igst_amount || 0), 0);

        const discount_amount = items.reduce((sum, i) => sum + parseFloat(i.discount_amount || 0), 0);

        const exd_tax_total = subtotal - discount_amount;

        const spl_disc_amount = (exd_tax_total * parseFloat(splDiscPercent || 0)) / 100;
        const after_spl_disc = exd_tax_total - spl_disc_amount;

        const with_tax = after_spl_disc + total_sgst + total_cgst + total_igst;

        const grand_total = with_tax + parseFloat(adjustment || 0) + parseFloat(additionalCharge || 0) + parseFloat(courierCharge || 0);

        // Calculate rounded values
        let rounded_grand_total = grand_total;
        let round_off = 0;

        if (enableRounding) {
            rounded_grand_total = roundAmount(grand_total);
            round_off = rounded_grand_total - grand_total;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            exd_tax_total: parseFloat(exd_tax_total.toFixed(2)),
            sgst: parseFloat(total_sgst.toFixed(2)),
            cgst: parseFloat(total_cgst.toFixed(2)),
            igst: parseFloat(total_igst.toFixed(2)),
            discount_amount: parseFloat(discount_amount.toFixed(2)),
            spl_disc_amount: parseFloat(spl_disc_amount.toFixed(2)),
            grand_total: parseFloat(grand_total.toFixed(2)),
            rounded_grand_total: parseFloat(rounded_grand_total.toFixed(2)),
            round_off: parseFloat(round_off.toFixed(2))
        };
    }, []);

    // Toggle rounding
    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                newEnableRounding
            );

            const paidAmount = prev.paid_amount;
            const grandTotal = newEnableRounding ? totals.rounded_grand_total : totals.grand_total;

            return reconcilePayment({
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals,
                paid_amount: paidAmount > grandTotal ? grandTotal : paidAmount,
                due_amount: grandTotal - (paidAmount > grandTotal ? grandTotal : paidAmount)
            });
        });
    };

    // Add item to sale
    const handleAddItem = async (product) => {
        const existingItem = formData.items.find(i => i.product_id === product.id);

        if (existingItem) {
            const index = formData.items.indexOf(existingItem);
            handleQuantityChange(index, existingItem.quantity + 1);
            setSearchTerm('');
            setShowProductSearch(false);
            return;
        }

        // Determine the unit price
        let unitPrice = parseFloat(product.selling_price || 0);
        let lastPriceInfo = null;

        // If customer is selected, try to get last price
        if (selectedCustomer && selectedCustomer.value) {
            const lastPriceData = await fetchLastPriceForCustomer(selectedCustomer.value, product.id);
            if (lastPriceData && lastPriceData.last_price > 0) {
                unitPrice = lastPriceData.last_price;
                lastPriceInfo = lastPriceData;

                // Show notification that we're using the last price
                Swal.fire({
                    icon: 'info',
                    title: 'Using Last Bill Price',
                    html: `Using last bill price from invoice <strong>${lastPriceData.invoice_no}</strong><br/>
                           Date: ${dayjs(lastPriceData.sale_date).format('DD/MM/YYYY')}<br/>
                           Price: <strong>₹${lastPriceData.last_price.toFixed(2)}</strong>`,
                    timer: 3000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
        }

        const taxPercent = parseFloat(product.tax_rate || 0);
        const sgstPercent = taxPercent / 2;
        const cgstPercent = taxPercent / 2;

        const subtotal = unitPrice * 1;
        const discountAmount = 0;
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = (taxableAmount * taxPercent) / 100;
        const sgstAmount = taxAmount / 2;
        const cgstAmount = taxAmount / 2;
        const totalCost = taxableAmount + taxAmount;

        const newItem = {
            id: `temp_${Date.now()}_${Math.random()}`,
            product_id: product.id,
            product_code: product.sku,
            product_name: product.product_name,
            hsn_code: product.hsn_code || '',
            sku: product.sku,
            units: 'PCS',
            quantity: 1,
            unit_price: unitPrice,
            original_price: product.selling_price, // Store original for reference
            last_price_info: lastPriceInfo,
            discount: 0,
            discount_amount: 0,
            tax_percent: taxPercent,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent,
            sgst_amount: sgstAmount,
            cgst_amount: cgstAmount,
            igst_amount: 0,
            unit_cost: parseFloat(product.cost_price || 0),
            total_cost: totalCost,
            category_id: product.category_id,
            brand_id: product.brand_id,
            available_stock: parseFloat(product.quantity || 0)
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });

        setSearchTerm('');
        setShowProductSearch(false);
    };

    // Handle quantity change
    const handleQuantityChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const actualQty = Math.max(0, qty);

            const subtotal = item.unit_price * actualQty;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                quantity: actualQty,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });
    };

    // Handle price change
    const handlePriceChange = (index, value) => {
        const price = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = price * item.quantity;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                unit_price: price,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });
    };

    // Handle discount change
    const handleDiscountChange = (index, value) => {
        const discountPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = item.unit_price * item.quantity;
            const discountAmount = (subtotal * discountPercent) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                discount: discountPercent,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });
    };

    // Handle tax change
    const handleTaxChange = (index, value) => {
        const taxPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const taxableAmount = item.unit_price * item.quantity - (item.discount_amount || 0);
            const taxAmount = (taxableAmount * taxPercent) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                tax_percent: taxPercent,
                sgst_percent: taxPercent / 2,
                cgst_percent: taxPercent / 2,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });
    };

    // Remove item
    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const updatedItems = prev.items.filter((_, i) => i !== index);
                    const totals = calculateTotals(
                        updatedItems,
                        prev.spl_disc_percent,
                        prev.adjustment,
                        prev.additional_charge,
                        prev.courier_charge,
                        prev.enable_rounding
                    );

                    return reconcilePayment({
                        ...prev,
                        items: updatedItems,
                        ...totals
                    });
                });
            }
        });
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));

        // Clear product cache when customer changes
        productCache.current.clear();

        // Re-search if there's a search term
        if (searchTerm.length >= 1) {
            searchProducts(searchTerm);
        }

        // Re-load the last bill price (for this customer) for items already added
        // to the table, so the rate column reflects the price last given to this
        // customer for each product. Falls back to the product's selling price.
        if (option && option.value) {
            applyLastPricesToExistingItems(option.value);
        }
    };

    // Fetch and apply each existing item's last bill price for the given customer.
    const applyLastPricesToExistingItems = async (customerId) => {
        const currentItems = formData.items;
        if (!currentItems || currentItems.length === 0) return;

        let appliedCount = 0;

        const updatedItems = await Promise.all(currentItems.map(async (item) => {
            if (!item.product_id) return item;

            const lastPriceData = await fetchLastPriceForCustomer(customerId, item.product_id);
            const newPrice = (lastPriceData && lastPriceData.last_price > 0)
                ? parseFloat(lastPriceData.last_price)
                : parseFloat(item.original_price || item.unit_price || 0);

            if (lastPriceData && lastPriceData.last_price > 0) {
                appliedCount += 1;
            }

            const subtotal = newPrice * (parseFloat(item.quantity) || 0);
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;

            return {
                ...item,
                unit_price: newPrice,
                last_price_info: lastPriceData || item.last_price_info || null,
                discount_amount: discountAmount,
                sgst_amount: taxAmount / 2,
                cgst_amount: taxAmount / 2,
                tax_amount: taxAmount,
                total_cost: taxableAmount + taxAmount
            };
        }));

        setFormData(prev => {
            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return reconcilePayment({
                ...prev,
                items: updatedItems,
                ...totals
            });
        });

        if (appliedCount > 0) {
            Swal.fire({
                icon: 'info',
                title: 'Last Bill Prices Applied',
                text: `Loaded last bill price for ${appliedCount} product(s) for this customer.`,
                timer: 2500,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
        }
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, sale_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'pending' }));
    };

    // Handle special discount percent change
    const handleSplDiscPercentChange = (value) => {
        const percent = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return reconcilePayment({
                ...prev,
                spl_disc_percent: percent,
                ...totals
            });
        });
    };

    // Handle adjustment change
    const handleAdjustmentChange = (value) => {
        const adjustment = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return reconcilePayment({
                ...prev,
                adjustment,
                ...totals
            });
        });
    };

    // Handle additional charge change
    const handleAdditionalChargeChange = (value) => {
        const charge = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return reconcilePayment({
                ...prev,
                additional_charge: charge,
                ...totals
            });
        });
    };

    // Handle courier charge change
    const handleCourierChargeChange = (value) => {
        const charge = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                charge,
                prev.enable_rounding
            );
            return reconcilePayment({
                ...prev,
                courier_charge: charge,
                ...totals
            });
        });
    };

    // Handle payment status change — auto-updates paid/due amounts based on selected status
    const handlePaymentStatusChange = (option) => {
        const status = option?.value || 'unpaid';
        // Clear any buffered raw text in the Paid Amount field so the auto-computed
        // amount is always shown after a status change.
        setRawInputValues(prev => { const c = { ...prev }; delete c.paid; return c; });
        setFormData(prev => reconcilePayment({ ...prev, payment_status: status }));
    };

    // Handle payment mode change
    const handlePaymentModeChange = (option) => {
        setFormData(prev => reconcilePayment({ ...prev, payment_mode: option?.value || '' }));
    };

    // Handle paid amount change
    const handlePaidAmountChange = (value) => {
        const paidAmount = parseFloat(value) || 0;
        const grandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

        setFormData(prev => {
            const validPaidAmount = Math.min(paidAmount, grandTotal);
            const dueAmount = grandTotal - validPaidAmount;

            let paymentStatus = 'unpaid';
            if (validPaidAmount >= grandTotal && grandTotal > 0) {
                paymentStatus = 'paid';
            } else if (validPaidAmount > 0) {
                paymentStatus = 'partial';
            } else if (CREDIT_TERM_STATUSES.includes(prev.payment_status)) {
                // Preserve credit-term status when nothing has been paid
                paymentStatus = prev.payment_status;
            }

            let paymentHistory = [];
            if (validPaidAmount > 0 && prev.payment_mode) {
                paymentHistory = [{
                    date: prev.sale_date,
                    amount: validPaidAmount,
                    method: prev.payment_mode,
                    reference: null,
                    notes: 'Initial payment',
                    status: 'completed'
                }];
            }

            return {
                ...prev,
                paid_amount: validPaidAmount,
                due_amount: dueAmount,
                payment_status: paymentStatus,
                payment_history: paymentHistory
            };
        });
    };

    // Handle customer added from modal
    const handleCustomerAdded = (newCustomer) => {
        console.log('Customer added callback received:', newCustomer);

        let customerData = newCustomer;
        if (newCustomer.data) {
            customerData = newCustomer.data;
        }
        if (newCustomer.customer) {
            customerData = newCustomer.customer;
        }

        if (!customerData || !customerData.id) {
            console.error('Invalid customer data received:', newCustomer);
            Swal.fire('Error', 'Invalid customer data received', 'error');
            return;
        }

        loadCustomers();

        const customerOption = {
            value: customerData.id,
            label: `${customerData.name} ${customerData.phone ? `(${customerData.phone})` : ''}`,
            phone: customerData.phone,
            email: customerData.email,
            address: customerData.address
        };

        setSelectedCustomer(customerOption);
        setFormData(prev => ({
            ...prev,
            customer_id: customerData.id,
            customer_name: customerOption.label,
            customer_phone: customerData.phone || ''
        }));

        Swal.fire({
            icon: 'success',
            title: 'Customer Added',
            text: `${customerData.name} has been added successfully`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    // Validate form
    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }

        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }

        // Stock availability check
        for (const item of formData.items) {
            const available = parseFloat(item.available_stock ?? Infinity);
            const requested = parseFloat(item.quantity || 0);
            if (requested > available) {
                Swal.fire({
                    icon: 'error',
                    title: 'Insufficient Stock',
                    html: `<b>${item.product_name}</b><br/>
                           You entered <b>${requested}</b> qty, but only <b>${available}</b> units are available in stock.`
                });
                return false;
            }
        }

        // Payment status consistency check
        const grandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const status = formData.payment_status;

        if (status === 'partial') {
            if (formData.paid_amount <= 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid Paid Amount',
                    text: 'Please enter a paid amount greater than ₹0 for Partial payment.'
                });
                return false;
            }
            if (formData.paid_amount >= grandTotal) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid Payment Status',
                    text: 'Paid amount equals or exceeds the Grand Total. Please select "Paid" status instead.'
                });
                return false;
            }
        }

        if ((status === 'advance_payment' || status === 'against_payment') && formData.paid_amount < grandTotal) {
            Swal.fire({
                icon: 'warning',
                title: 'Payment Mismatch',
                text: `"${status === 'advance_payment' ? 'Advance Payment' : 'Against Payment'}" requires the full amount to be paid. Paid: ₹${formData.paid_amount}, Due: ₹${formData.due_amount}`
            });
            return false;
        }

        // Payment mode is mandatory whenever money is being collected
        const modeRequiredStatuses = ['advance_payment', 'against_payment', 'paid', 'partial'];
        if (modeRequiredStatuses.includes(status) && !formData.payment_mode) {
            Swal.fire({
                icon: 'warning',
                title: 'Payment Mode Required',
                text: 'Please select a Payment Mode for the selected payment status.'
            });
            return false;
        }

        return true;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setLoading(true);

            const itemsToSend = formData.items.map(({ ...item }) => item);
            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                ...formData,
                items: itemsToSend,
                grand_total: finalGrandTotal,
                round_off: formData.round_off,
                payment_history: formData.payment_history,
                paid_amount: Math.min(formData.paid_amount, finalGrandTotal),
                due_amount: finalGrandTotal - Math.min(formData.paid_amount, finalGrandTotal)
            };

            console.log('Sending data:', JSON.stringify(dataToSend, null, 2));

            const result = await saleActions.createSale(dataToSend);

            if (result.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Sale created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate('/sales-list');
            } else {
                console.error('Error response:', result);
                Swal.fire('Error', result.message || 'Failed to create sale', 'error');
            }
        } catch (error) {
            console.error('Error creating sale:', error);
            Swal.fire('Error', error.message || 'Failed to create sale', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (formData.items.length > 0 || formData.customer_id) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/sales-list');
                }
            });
        } else {
            navigate('/sales-list');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // ── Numeric input helpers (raw-string pattern) ──
    // Returns the raw typed string while editing, else the numeric value as string
    const getRaw = (key, numericVal) =>
        key in rawInputValues ? rawInputValues[key] : String(numericVal ?? 0);

    // On focus: if value is 0, clear so user can start typing immediately
    const onNumFocus = (key, numericVal) => {
        if (parseFloat(numericVal) === 0) {
            setRawInputValues(prev => ({ ...prev, [key]: '' }));
        }
    };

    // On blur: if still empty, remove the raw key (reverts display to numeric value)
    const onNumBlur = (key) => {
        const raw = rawInputValues[key];
        if (raw === '' || raw === undefined) {
            setRawInputValues(prev => { const c = { ...prev }; delete c[key]; return c; });
        }
    };

    // On change: store raw string + call the numeric change handler
    const onNumChange = (key, value, numericSetter) => {
        setRawInputValues(prev => ({ ...prev, [key]: value }));
        numericSetter(value);
    };

    // Cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => {
            if (productSearchTimeout.current) {
                clearTimeout(productSearchTimeout.current);
            }
        };
    }, []);

    // Clear raw display values when item rows change (add/remove)
    useEffect(() => {
        setRawInputValues({});
    }, [formData.items.length]);

    // Get the current grand total based on rounding setting
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* ── Page Header ── */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>New Sale</h4>
                        <h6>Create sales invoice</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
                                <ArrowLeft size={15} className="me-1" /> Back
                            </button>
                        </li>
                    </ul>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* ── Section 1: Invoice Meta ── */}
                    <div className="card mb-3">
                        <div className="card-body py-3">
                            <div className="row g-3 align-items-end">
                                {/* Invoice No */}
                                <div className="col-md-2">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">Invoice No</label>
                                    <input className="form-control form-control-sm bg-light" value={formData.sales_reference} readOnly />
                                </div>

                                {/* Date */}
                                <div className="col-md-2">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">Date</label>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="datetimepicker"
                                        style={{ width: '100%' }}
                                        allowClear={false}
                                        getPopupContainer={trigger => trigger.parentElement}
                                    />
                                </div>

                                {/* Customer */}
                                <div className="col-md-3">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">
                                        Customer <span className="text-danger">*</span>
                                    </label>
                                    <div className="d-flex gap-1">
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={customers}
                                                value={selectedCustomer}
                                                onChange={handleCustomerChange}
                                                onInputChange={handleCustomerSearch}
                                                placeholder="Search customer…"
                                                isClearable
                                                isLoading={loadingCustomers}
                                                noOptionsMessage={() => customerError || 'No customers found'}
                                                styles={{ control: (b) => ({ ...b, minHeight: '31px', fontSize: '0.875rem' }) }}
                                            />
                                        </div>
                                        <button type="button" className="btn btn-outline-primary btn-sm px-2" onClick={() => setShowCustomerModal(true)} title="Add new customer">
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    {customerError && <small className="text-danger">{customerError}</small>}
                                </div>

                                {/* Phone */}
                                <div className="col-md-2">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">Phone</label>
                                    <input
                                        className="form-control form-control-sm"
                                        value={formData.customer_phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                                        placeholder="Phone"
                                    />
                                </div>

                                {/* PO Number */}
                                <div className="col-md-1">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">PO No</label>
                                    <input className="form-control form-control-sm" value={formData.po_number} onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))} placeholder="PO" />
                                </div>

                                {/* DC Number */}
                                <div className="col-md-2">
                                    <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">DC Number</label>
                                    <input className="form-control form-control-sm" value={formData.dc_number} onChange={(e) => setFormData(prev => ({ ...prev, dc_number: e.target.value }))} placeholder="DC number" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Section 2: Product Search ── */}
                    <div className="card mb-3">
                        <div className="card-body py-3">
                            <label className="form-label fw-semibold mb-1 small text-muted text-uppercase">
                                Add Product — Search by Name or Code
                            </label>
                            <div className="position-relative" ref={searchDropdownRef}>
                                <div className="input-group">
                                    <span className="input-group-text bg-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                    </span>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="form-control"
                                        placeholder="Type product name or code to search…"
                                        value={searchTerm}
                                        onChange={handleProductSearch}
                                        autoComplete="off"
                                    />
                                    {searching && (
                                        <span className="input-group-text bg-white">
                                            <div className="spinner-border spinner-border-sm text-primary" />
                                        </span>
                                    )}
                                </div>

                                {/* Dropdown results */}
                                {showProductSearch && searchResults.length > 0 && (
                                    <div className="position-absolute w-100 mt-1 bg-white border rounded shadow" style={{ zIndex: 9999, maxHeight: '280px', overflowY: 'auto' }}>
                                        {searchResults.map(product => (
                                            <div
                                                key={product.id}
                                                className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                                                onClick={() => handleAddItem(product)}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f5f7ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                            >
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{product.product_name}</div>
                                                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                                        Code: <strong>{product.sku}</strong>
                                                        {product.has_last_price && (
                                                            <span className="ms-2 badge bg-info-subtle text-info">
                                                                Last: ₹{product.last_price_data?.last_price?.toFixed(2)} ({dayjs(product.last_price_data?.sale_date).format('DD/MM/YY')})
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    {product.has_last_price ? (
                                                        <>
                                                            <div className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>₹{product.last_price_data?.last_price?.toFixed(2)}</div>
                                                            <div className="text-muted text-decoration-line-through" style={{ fontSize: '0.76rem' }}>₹{product.selling_price?.toFixed(2)}</div>
                                                        </>
                                                    ) : (
                                                        <div className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>₹{product.selling_price?.toFixed(2)}</div>
                                                    )}
                                                    <span className="badge bg-success-subtle text-success" style={{ fontSize: '0.72rem' }}>Stock: {product.quantity}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {showProductSearch && searchResults.length === 0 && !searching && outOfStockMessage && (
                                    <div className="position-absolute w-100 mt-1 bg-white border rounded shadow px-3 py-2" style={{ zIndex: 9999, fontSize: '0.875rem' }}>
                                        <span className="text-danger d-flex align-items-center">
                                            <i className="feather icon-alert-triangle me-1" />
                                            {outOfStockMessage}
                                        </span>
                                    </div>
                                )}
                                {showProductSearch && searchResults.length === 0 && !searching && !outOfStockMessage && searchTerm.length >= 1 && (
                                    <div className="position-absolute w-100 mt-1 bg-white border rounded shadow px-3 py-2 text-muted" style={{ zIndex: 9999, fontSize: '0.875rem' }}>
                                        No products found for <strong>{searchTerm}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Section 3: Product Table ── */}
                    {formData.items.length > 0 && (
                        <div className="card mb-3">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                                        <thead style={{ background: '#f0f4ff' }}>
                                            <tr>
                                                <th style={{ width: '30px' }}>#</th>
                                                <th style={{ minWidth: '90px' }}>Code</th>
                                                <th style={{ minWidth: '200px' }}>Product Name</th>
                                                <th style={{ width: '80px' }}>HSN</th>
                                                <th style={{ width: '80px' }}>Unit</th>
                                                <th className="text-center" style={{ width: '80px' }}>Qty</th>
                                                <th className="text-end" style={{ width: '110px' }}>Rate (Excl.Tax)</th>
                                                <th className="text-center" style={{ width: '70px' }}>Disc %</th>
                                                <th className="text-center" style={{ width: '70px' }}>SGST %</th>
                                                <th className="text-center" style={{ width: '70px' }}>CGST %</th>
                                                <th className="text-end" style={{ width: '110px' }}>Amount</th>
                                                <th className="text-center" style={{ width: '50px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="text-muted align-middle">{index + 1}</td>
                                                    <td className="align-middle">
                                                        <span className="badge bg-secondary-subtle text-secondary fw-normal">{item.product_code || item.sku || '—'}</span>
                                                    </td>
                                                    <td className="align-middle">
                                                        <div className="fw-semibold">{item.product_name}</div>
                                                        {item.last_price_info && (
                                                            <small className="text-info" style={{ fontSize: '0.74rem' }}>
                                                                Last bill: ₹{item.last_price_info.last_price?.toFixed(2)}
                                                            </small>
                                                        )}
                                                        {item.available_stock !== undefined && (
                                                            <small className={`d-block ${parseFloat(item.quantity) > parseFloat(item.available_stock) ? 'text-danger fw-bold' : 'text-muted'}`} style={{ fontSize: '0.72rem' }}>
                                                                Stock: {item.available_stock}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td className="align-middle">
                                                        <input type="text" className="form-control form-control-sm bg-light" value={item.hsn_code || ''} readOnly style={{ width: '70px' }} />
                                                    </td>
                                                    <td className="align-middle">
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.units}
                                                            onChange={(e) => {
                                                                const updated = [...formData.items];
                                                                updated[index] = { ...updated[index], units: e.target.value };
                                                                setFormData(prev => ({ ...prev, items: updated }));
                                                            }}
                                                            style={{ width: '72px' }}
                                                        >
                                                            <option>PCS</option>
                                                            <option>BOX</option>
                                                            <option>KG</option>
                                                            <option>LTR</option>
                                                        </select>
                                                    </td>
                                                    <td className="align-middle">
                                                        <input
                                                            type="number" min="0" step="any"
                                                            className={`form-control form-control-sm text-center ${parseFloat(item.quantity) > parseFloat(item.available_stock ?? Infinity) ? 'border-danger' : ''}`}
                                                            value={getRaw(`qty_${index}`, item.quantity)}
                                                            onChange={(e) => onNumChange(`qty_${index}`, e.target.value, (v) => handleQuantityChange(index, v))}
                                                            onFocus={() => onNumFocus(`qty_${index}`, item.quantity)}
                                                            onBlur={() => onNumBlur(`qty_${index}`)}
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <input
                                                            type="number" min="0" step="any"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`price_${index}`, item.unit_price)}
                                                            onChange={(e) => onNumChange(`price_${index}`, e.target.value, (v) => handlePriceChange(index, v))}
                                                            onFocus={() => onNumFocus(`price_${index}`, item.unit_price)}
                                                            onBlur={() => onNumBlur(`price_${index}`)}
                                                            style={{ width: '100px' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <input
                                                            type="number" min="0" step="any"
                                                            className="form-control form-control-sm text-center"
                                                            value={getRaw(`disc_${index}`, item.discount || 0)}
                                                            onChange={(e) => onNumChange(`disc_${index}`, e.target.value, (v) => handleDiscountChange(index, v))}
                                                            onFocus={() => onNumFocus(`disc_${index}`, item.discount || 0)}
                                                            onBlur={() => onNumBlur(`disc_${index}`)}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <input
                                                            type="number" min="0" step="any"
                                                            className="form-control form-control-sm text-center"
                                                            value={getRaw(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onChange={(e) => { const val = e.target.value; onNumChange(`sgst_${index}`, val, (v) => handleTaxChange(index, (parseFloat(v) || 0) * 2)); }}
                                                            onFocus={() => onNumFocus(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onBlur={() => onNumBlur(`sgst_${index}`)}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <input
                                                            type="number" min="0" step="any"
                                                            className="form-control form-control-sm text-center"
                                                            value={getRaw(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onChange={(e) => { const val = e.target.value; onNumChange(`cgst_${index}`, val, (v) => handleTaxChange(index, (parseFloat(v) || 0) * 2)); }}
                                                            onFocus={() => onNumFocus(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onBlur={() => onNumBlur(`cgst_${index}`)}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </td>
                                                    <td className="align-middle text-end fw-bold">
                                                        {formatCurrency(item.total_cost)}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        <button type="button" className="btn btn-outline-danger btn-sm p-1" onClick={() => handleRemoveItem(index)}>
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Section 4: Totals & Payment ── */}
                    <div className="card mb-3">
                        <div className="card-body py-3">
                            <div className="row g-3">
                                {/* Left — summary strip */}
                                <div className="col-md-7">
                                    <div className="row g-2 mb-2">
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">Items</div>
                                                <div className="fw-bold text-primary">{formData.no_of_items}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">Total Qty</div>
                                                <div className="fw-bold text-info">{formData.total_qty}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">Subtotal</div>
                                                <div className="fw-bold text-success">{formatCurrency(formData.subtotal)}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">Discount</div>
                                                <div className="fw-bold text-danger">{formatCurrency(formData.discount_amount)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-2">
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">SGST</div>
                                                <div className="fw-bold">{formatCurrency(formData.sgst)}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">CGST</div>
                                                <div className="fw-bold">{formatCurrency(formData.cgst)}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">IGST</div>
                                                <div className="fw-bold">{formatCurrency(formData.igst)}</div>
                                            </div>
                                        </div>
                                        <div className="col-6 col-sm-3">
                                            <div className="border rounded p-2 text-center">
                                                <div className="small text-muted">Exd. Tax</div>
                                                <div className="fw-bold text-success">{formatCurrency(formData.exd_tax_total)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Adjustments row */}
                                    <div className="row g-2">
                                        <div className="col-sm-4">
                                            <label className="form-label small fw-semibold mb-1">Spl Disc %</label>
                                            <div className="input-group input-group-sm">
                                                <input type="number" className="form-control text-end"
                                                    value={getRaw('spl_disc', formData.spl_disc_percent)}
                                                    onChange={(e) => onNumChange('spl_disc', e.target.value, handleSplDiscPercentChange)}
                                                    onFocus={() => onNumFocus('spl_disc', formData.spl_disc_percent)}
                                                    onBlur={() => onNumBlur('spl_disc')} step="any" />
                                                <span className="input-group-text">%</span>
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <label className="form-label small fw-semibold mb-1">Adjustment (+/-)</label>
                                            <input type="number" className="form-control form-control-sm text-end"
                                                value={getRaw('adjustment', formData.adjustment ?? 0)}
                                                onChange={(e) => onNumChange('adjustment', e.target.value, handleAdjustmentChange)}
                                                onFocus={() => onNumFocus('adjustment', formData.adjustment ?? 0)}
                                                onBlur={() => onNumBlur('adjustment')} step="any" />
                                        </div>
                                        <div className="col-sm-4">
                                            <label className="form-label small fw-semibold mb-1">Additional Charge</label>
                                            <input type="number" className="form-control form-control-sm text-end" min="0" step="any"
                                                value={getRaw('add_charge', formData.additional_charge)}
                                                onChange={(e) => onNumChange('add_charge', e.target.value, handleAdditionalChargeChange)}
                                                onFocus={() => onNumFocus('add_charge', formData.additional_charge)}
                                                onBlur={() => onNumBlur('add_charge')} />
                                        </div>
                                        <div className="col-sm-4">
                                            <label className="form-label small fw-semibold mb-1">Courier / Shipping</label>
                                            <input type="number" className="form-control form-control-sm text-end" min="0" step="any"
                                                value={getRaw('courier', formData.courier_charge)}
                                                onChange={(e) => onNumChange('courier', e.target.value, handleCourierChargeChange)}
                                                onFocus={() => onNumFocus('courier', formData.courier_charge)}
                                                onBlur={() => onNumBlur('courier')} />
                                        </div>
                                    </div>
                                </div>

                                {/* Right — Grand Total + Payment */}
                                <div className="col-md-5">
                                    {/* Grand Total */}
                                    <div className="rounded p-3 mb-3 text-white d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg,#fc6c00,#e65c00)' }}>
                                        <div>
                                            <div className="small opacity-75">Grand Total</div>
                                            <h3 className="mb-0 fw-bold text-white">{formatCurrency(currentGrandTotal)}</h3>
                                            {formData.enable_rounding && formData.round_off !== 0 && (
                                                <small className="opacity-75">Actual: {formatCurrency(formData.grand_total)} | Round off: {formData.round_off > 0 ? '+' : ''}{formatCurrency(formData.round_off)}</small>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light btn-outline-light"
                                            onClick={handleToggleRounding}
                                            title={formData.enable_rounding ? 'Rounding ON' : 'Rounding OFF'}
                                        >
                                            <RotateCcw size={13} className="me-1" />
                                            {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                        </button>
                                    </div>

                                    {/* Payment fields */}
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <label className="form-label small fw-semibold mb-1">Paid Amount</label>
                                            <input type="number" className="form-control form-control-sm text-end" min="0" step="any"
                                                value={getRaw('paid', formData.paid_amount)}
                                                onChange={(e) => onNumChange('paid', e.target.value, handlePaidAmountChange)}
                                                onFocus={() => onNumFocus('paid', formData.paid_amount)}
                                                onBlur={() => onNumBlur('paid')} />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small fw-semibold mb-1">Due Amount</label>
                                            <input type="text" className={`form-control form-control-sm text-end fw-bold bg-light ${formData.due_amount > 0 ? 'text-danger' : 'text-success'}`}
                                                value={formatCurrency(formData.due_amount)} readOnly />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small fw-semibold mb-1">
                                                Payment Mode
                                                {['advance_payment', 'against_payment', 'paid', 'partial'].includes(formData.payment_status) && (
                                                    <span className="text-danger ms-1">*</span>
                                                )}
                                            </label>
                                            <Select
                                                options={paymentModeOptions}
                                                value={paymentModeOptions.find(opt => opt.value === formData.payment_mode) || null}
                                                onChange={handlePaymentModeChange}
                                                placeholder="Mode…"
                                                isClearable
                                                styles={{ control: (b) => ({ ...b, minHeight: '31px', fontSize: '0.875rem' }) }}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small fw-semibold mb-1">Payment Status</label>
                                            <Select
                                                options={paymentStatusOptions}
                                                value={paymentStatusOptions.find(opt => opt.value === formData.payment_status) || null}
                                                onChange={handlePaymentStatusChange}
                                                placeholder="Status…"
                                                isClearable
                                                styles={{ control: (b) => ({ ...b, minHeight: '31px', fontSize: '0.875rem' }) }}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small fw-semibold mb-1">Order Status</label>
                                            <Select
                                                options={statusOptions}
                                                value={statusOptions.find(opt => opt.value === formData.status) || null}
                                                onChange={handleStatusChange}
                                                placeholder="Status…"
                                                styles={{ control: (b) => ({ ...b, minHeight: '31px', fontSize: '0.875rem' }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Section 5: Remarks + Actions ── */}
                    <div className="card mb-3">
                        <div className="card-body py-3">
                            <div className="row g-3 align-items-end">
                                <div className="col-md-8">
                                    <label className="form-label small fw-semibold mb-1 text-muted text-uppercase">Remarks</label>
                                    <textarea className="form-control form-control-sm" rows="2"
                                        value={formData.remarks}
                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                        placeholder="Any additional remarks…" />
                                </div>
                                <div className="col-md-4 d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-outline-secondary" onClick={handleCancel}>
                                        <X size={15} className="me-1" /> Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading || formData.items.length === 0}
                                    >
                                        {loading ? (
                                            <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                                        ) : (
                                            <><Save size={15} className="me-1" />Save Sale</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Add Customer Modal */}
            {showCustomerModal && (
                <AddCustomer
                    isOpen={showCustomerModal}
                    onClose={() => setShowCustomerModal(false)}
                    onSuccess={handleCustomerAdded}
                />
            )}
        </div>
    );
};

export default AddSalePage;
